/**
 * ZATCA (هيئة الزكاة والضريبة والجمارك) — QR code payload builder.
 *
 * The simplified tax invoice QR encodes a Base64 string whose bytes are a
 * sequence of TLV (Tag / Length / Value) fields:
 *   1 → Seller name
 *   2 → VAT registration number
 *   3 → Invoice timestamp (ISO 8601)
 *   4 → Invoice total (with VAT)
 *   5 → VAT total
 */

export interface ZatcaQrPayload {
  sellerName: string;
  vatNumber: string;
  timestamp: string;
  totalWithVat: number;
  vatTotal: number;
}

const toBytes = (value: string): Uint8Array => new TextEncoder().encode(value);

const tlvField = (tag: number, value: Uint8Array): Uint8Array => {
  const field = new Uint8Array(2 + value.length);
  field[0] = tag;
  field[1] = value.length;
  field.set(value, 2);
  return field;
};

export const buildZatcaQrValue = (payload: ZatcaQrPayload): string => {
  const fields = [
    tlvField(1, toBytes(payload.sellerName)),
    tlvField(2, toBytes(payload.vatNumber)),
    tlvField(3, toBytes(payload.timestamp)),
    tlvField(4, toBytes(payload.totalWithVat.toFixed(2))),
    tlvField(5, toBytes(payload.vatTotal.toFixed(2))),
  ];

  const totalLength = fields.reduce((sum, field) => sum + field.length, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;
  fields.forEach((field) => {
    merged.set(field, offset);
    offset += field.length;
  });

  let binary = "";
  merged.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary);
};